import React, {useEffect, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {Input, Tag, Text} from '../../../sharedComponents/components'
import {colors} from '../../../sharedComponents/colors'

import {EventPayload} from '../../../state/rest'
import {useTranslation} from 'react-i18next'

interface InputWithTagsProps {
  defaultSuggestions: string[]
  onChange: (key: keyof EventPayload, selected: string[]) => void
  value?: string
  label?: string
  keyName?: keyof EventPayload
  savedSuggestions?: string | string[]
  multiple?: boolean
  placeholder?: string
  required?: boolean
  disabled?: boolean
  /**
   * When provided, restricts input to this many characters and shows a right-aligned counter (x/max).
   */
  maxCharacters?: number
}

const InputWithTags = ({
  keyName,
  defaultSuggestions,
  onChange,
  savedSuggestions,
  value,
  label,
  placeholder,
  multiple = false,
  required,
  disabled = false,
  maxCharacters
}: InputWithTagsProps) => {
  const characterLimit = typeof maxCharacters === 'number' ? maxCharacters : undefined
  const normalizedSavedSuggestions = useMemo(() => {
    if (Array.isArray(savedSuggestions)) {
      return savedSuggestions.filter(ss => !!ss)
    }
    if (savedSuggestions) {
      return [savedSuggestions]
    }

    return []
  }, [savedSuggestions])

  const derivedSelected = useMemo(() => {
    let selectedIndices = normalizedSavedSuggestions
      .map(ss => defaultSuggestions.findIndex(ds => ds === ss))
      .filter(index => index !== -1)

    if (!multiple) {
      selectedIndices = selectedIndices.slice(0, 1)
    }

    return selectedIndices
  }, [defaultSuggestions, multiple, normalizedSavedSuggestions])

  const derivedTextValue = useMemo(() => {
    const nextValue =
      value !== undefined
        ? value
        : normalizedSavedSuggestions.filter(ss => !defaultSuggestions.includes(ss)).join(',') || ''

    return characterLimit ? nextValue.slice(0, characterLimit) : nextValue
  }, [characterLimit, defaultSuggestions, normalizedSavedSuggestions, value])

  const [selected, setSelected] = useState<number[]>(derivedSelected)
  const [textValue, setTextValue] = useState(derivedTextValue)
  const localEditPendingRef = useRef(false)

  useEffect(() => {
    if (localEditPendingRef.current) {
      localEditPendingRef.current = false
      return
    }
    setSelected(derivedSelected)
    setTextValue(derivedTextValue)
  }, [derivedSelected, derivedTextValue])

  const {t} = useTranslation('tasks')

  const handleSelect = (index: number) => {
    let selectedIndices = []
    if (!selected.includes(index)) {
      selectedIndices = multiple ? [...selected, index] : [index]
    } else {
      selectedIndices = selected.filter(s => s !== index)
    }
    setSelected(selectedIndices)
    const selectedTags = selectedIndices.map(s => defaultSuggestions[s])
    onChange(keyName || 'suggestions', selectedTags)
    setTextValue('')
  }

  const handleText = (e: React.ChangeEvent<HTMLInputElement> & React.ChangeEvent<HTMLTextAreaElement>) => {
    localEditPendingRef.current = true
    const nextValue = characterLimit ? e.target.value.slice(0, characterLimit) : e.target.value
    setTextValue(nextValue)
    const selectedTags = selected.map(s => defaultSuggestions[s])
    onChange(keyName || 'suggestions', nextValue ? selectedTags.concat(nextValue) : selectedTags)
  }
  const defaultLabelText = t('tasks:labels.measurementTarget', 'What are you measuring?')
  const selectedTags = selected.map(index => defaultSuggestions[index]).filter(Boolean)
  const displayValue = selectedTags.length > 0 ? selectedTags.join(', ') : textValue
  const currentCount = displayValue?.length || 0
  return (
    <Wrapper>
      <Input
        name={'customSuggestion' + label}
        id={'customSuggestion' + label}
        label={label || defaultLabelText}
        placeholder={placeholder || defaultLabelText}
        onChange={handleText}
        value={displayValue}
        maxLength={characterLimit}
        disabled={disabled || selected.length > 0}
        bottomArea={
          <TagsContainer>
            {defaultSuggestions.map((ds, index) => (
              <Tag
                disabled={disabled || (textValue.length > 0 && selected.length === 0)}
                styleType={selected.includes(index) ? 'active' : 'default'}
                key={index}
                onClick={() => handleSelect(index)}
              >
                {ds}
              </Tag>
            ))}
          </TagsContainer>
        }
        required={required}
      />
      {characterLimit ? (
        <CounterRow>
          <Text size="XS" color={colors.system.grey_50}>
            {`${currentCount}/${characterLimit}`}
          </Text>
        </CounterRow>
      ) : null}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const CounterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.25rem;
`

const TagsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`
export default InputWithTags
